import { motion } from "framer-motion";
import { Check, Clock, Target } from "lucide-react";

const ROADMAP_IMAGES = [
  "https://customer-assets.emergentagent.com/job_meme-coin-hub-10/artifacts/xqeyzgpc_mojo-jojo-feature-image-768x549.png",
  "https://customer-assets.emergentagent.com/job_meme-coin-hub-10/artifacts/2wzb07lr_powerpuff-girls-with-mojo-jojo-jhn77f92tviltyph.jpg",
  "https://customer-assets.emergentagent.com/job_meme-coin-hub-10/artifacts/o4qoypn3_the-powerpuff-girls-beat-up-mojo-jojo.jpg",
];

const phases = [
  {
    phase: "Phase 1",
    title: "The Inception",
    subtitle: "Building the Foundation",
    status: "completed",
    image: ROADMAP_IMAGES[0],
    items: [
      "Token Launch on Ethereum",
      "Contract Verification on Etherscan",
      "Website & Social Media Launch",
      "DexTools & DexScreener Listing",
      "Initial Community Building",
      "1,000+ Holders Milestone",
    ],
  },
  {
    phase: "Phase 2",
    title: "The Domination",
    subtitle: "Expanding the Empire",
    status: "current",
    image: ROADMAP_IMAGES[1],
    items: [
      "CoinGecko & CoinMarketCap Listing",
      "Influencer Partnerships",
      "10,000+ Holders Milestone",
      "Community Governance Launch",
      "NFT Collection Announcement",
      "Strategic Exchange Partnerships",
    ],
  },
  {
    phase: "Phase 3",
    title: "Townsville Takeover",
    subtitle: "Global Expansion",
    status: "upcoming",
    image: ROADMAP_IMAGES[2],
    items: [
      "Tier 1 CEX Listings",
      "Cross-chain Bridge Integration",
      "Mojo Jojo Metaverse Concept",
      "Global Marketing Blitz",
      "100,000+ Holders Milestone",
      "World Domination Complete",
    ],
  },
];

const getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return <Check size={16} className="text-green-400" />;
    case "current":
      return <Clock size={16} className="text-[#FFD700]" />;
    default:
      return <Target size={16} className="text-[#9D4CDD]" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "border-green-400/50 bg-green-400/10";
    case "current":
      return "border-[#FFD700]/50 bg-[#FFD700]/10";
    default:
      return "border-[#9D4CDD]/30 bg-[#9D4CDD]/5";
  }
};

const Roadmap = () => {
  return (
    <section id="roadmap" className="py-24 px-6 md:px-12 bg-[#05000A]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-[#9D4CDD] font-body text-xs tracking-[0.3em] uppercase mb-4">
            The Master Plan
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Roadmap to <span className="text-[#FFD700]">World Domination</span>
          </h2>
          <p className="text-[#A199AA] font-body mt-4 max-w-2xl mx-auto">
            Every great villain needs a master plan. Here's ours.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative" data-testid="roadmap-timeline">
          {/* Vertical Line */}
          <div className="hidden md:block absolute left-8 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#9D4CDD] via-[#FFD700] to-[#9D4CDD]" />

          {/* Phases */}
          <div className="space-y-12">
            {phases.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
                data-testid={`roadmap-phase-${index + 1}`}
              >
                {/* Timeline Dot */}
                <div className="hidden md:flex absolute left-0 w-16 h-16 items-center justify-center">
                  <div className={`timeline-dot ${phase.status === 'current' ? 'animate-pulse' : ''}`} />
                </div>

                {/* Content Card */}
                <div className="md:ml-24 bento-card">
                  <div className="grid md:grid-cols-12 gap-6">
                    {/* Phase Info */}
                    <div className="md:col-span-8 space-y-4">
                      {/* Header */}
                      <div className="flex flex-wrap items-center gap-4">
                        <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusColor(phase.status)}`}>
                          <span className="flex items-center gap-2">
                            {getStatusIcon(phase.status)}
                            {phase.status === "completed" ? "Complete" : phase.status === "current" ? "In Progress" : "Upcoming"}
                          </span>
                        </span>
                        <span className="text-[#FFD700] font-heading font-bold">
                          {phase.phase}
                        </span>
                      </div>

                      {/* Title */}
                      <div>
                        <h3 className="font-heading text-2xl font-bold text-white">
                          {phase.title}
                        </h3>
                        <p className="text-[#A199AA] text-sm">
                          {phase.subtitle}
                        </p>
                      </div>

                      {/* Items */}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {phase.items.map((item, itemIndex) => (
                          <li 
                            key={itemIndex}
                            className="flex items-start gap-2 text-sm"
                          >
                            <span className={`mt-1 w-1.5 h-1.5 flex-shrink-0 ${phase.status === 'completed' ? 'bg-green-400' : phase.status === 'current' ? 'bg-[#FFD700]' : 'bg-[#9D4CDD]'}`} />
                            <span className={phase.status === 'completed' ? 'text-[#A199AA] line-through' : 'text-white'}>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Phase Image */}
                    <div className="md:col-span-4 flex items-center justify-center">
                      <div className="relative w-32 h-32 md:w-40 md:h-40">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#9D4CDD]/30 to-[#FFD700]/30 rounded-full blur-xl" />
                        <img
                          src={phase.image}
                          alt={`${phase.title} illustration`}
                          className="relative z-10 w-full h-full object-cover rounded-lg border border-[#9D4CDD]/30"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
